import * as React from "react";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { GridActionsCellItem, GridToolbarContainer } from "@mui/x-data-grid";
import { default as DataGrid } from "../../../../components/LocalizedDataGrid";
import { useTranslation } from "react-i18next";
import MeteringTariffEditPopUp, {
    PRICE_ENGINE_TO_TEXT,
} from "./MeteringTariffEditPopUp";
import { ApiContext } from "../../../../App";

function TariffToolbar(props) {
    const { onAddClick } = props;

    const { t } = useTranslation();

    return (
        <GridToolbarContainer>
            <Button color="primary" startIcon={<AddIcon />} onClick={onAddClick}>
                {t("Add Tariff")}
            </Button>
        </GridToolbarContainer>
    );
}

export default function MeteringTariffEditor(props) {
    const { facility } = props;

    const { t } = useTranslation();

    const [rows, setRows] = React.useState([]);
    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
    const [deleteCandidate, setDeleteCandidate] = React.useState();
    const [editPopUpOpen, setEditPopUpOpen] = React.useState(false);
    const [editPopUpData, setEditPopUpData] = React.useState({});

    const { meteringTariffApi } = React.useContext(ApiContext);

    React.useEffect(() => {
        meteringTariffApi.list(
            facility,
            (data) => {
                setRows(data);
            },
            () => {
                setAlertMessage(t("Couldn't connect to server."));
                setAlertOpen(true);
            },
        );
    }, [facility]);

    const handleAddClick = () => {
        setEditPopUpData({ facility: Number(facility.split("/").slice(-2)[0]) });
        setEditPopUpOpen(true);
    };

    const handleEditClick = (id) => {
        // The API sends decimals with trailing zeros, e.g. "0.10000000".
        const row = rows.find((item) => item.id === id);
        setEditPopUpData({
            ...row,
            value: row.value === null ? null : String(Number(row.value)),
            night_value:
                row.night_value === null ? null : String(Number(row.night_value)),
        });
        setEditPopUpOpen(true);
    };

    const handleEditPopUpClosedWithUpdate = (saved) => {
        if (rows.find((item) => item.id === saved.id))
            setRows(rows.map((row) => (row.id === saved.id ? saved : row)));
        else setRows((oldRows) => [saved, ...oldRows]);
        setEditPopUpOpen(false);
    };

    const handleDeleteClick = (id) => {
        setDeleteCandidate(id);
        setDeleteConfirmOpen(true);
    };

    const handleDelete = (id) => {
        meteringTariffApi.delete(
            `${meteringTariffApi.baseUrl}${id}/`,
            () => {
                setRows(rows.filter((row) => row.id !== id));
            },
            () => {
                setAlertMessage(
                    t(
                        "The tariff couldn't be deleted. Tariffs used by points or with energy prices can't be deleted.",
                    ),
                );
                setAlertOpen(true);
            },
        );
        setDeleteCandidate(null);
    };

    const decimal = ({ value }) => (value === null ? "" : Number(value));
    const time = ({ value }) => (value === null ? "" : value.slice(0, 5));

    const columns = [
        { field: "name", headerName: t("Name"), flex: 1, minWidth: 150 },
        {
            field: "price_engine",
            headerName: t("Price engine"),
            flex: 1,
            minWidth: 120,
            valueFormatter: ({ value }) => t(PRICE_ENGINE_TO_TEXT[value] ?? value),
        },
        {
            field: "value",
            headerName: t("Value"),
            flex: 0.5,
            minWidth: 80,
            valueFormatter: decimal,
        },
        {
            field: "night_value",
            headerName: t("Night Value"),
            flex: 0.5,
            minWidth: 80,
            valueFormatter: decimal,
        },
        {
            field: "day_tariff_start_time",
            headerName: t("Day tariff start time"),
            flex: 0.7,
            minWidth: 100,
            valueFormatter: time,
        },
        {
            field: "day_tariff_end_time",
            headerName: t("Day tariff end time"),
            flex: 0.7,
            minWidth: 100,
            valueFormatter: time,
        },
        {
            field: "carbon_intensity_g_kwh",
            headerName: t("Carbon intensity (g/kWh)"),
            flex: 0.7,
            minWidth: 100,
        },
        {
            field: "actions",
            type: "actions",
            headerName: t("Actions"),
            flex: 0.5,
            minWidth: 100,
            cellClassName: "actions",
            getActions: ({ id }) => {
                return [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label={t("Edit")}
                        className="textPrimary"
                        onClick={() => handleEditClick(id)}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        icon={<DeleteIcon />}
                        label={t("Delete")}
                        onClick={() => handleDeleteClick(id)}
                        color="inherit"
                    />,
                ];
            },
        },
    ];

    return (
        <>
            <MeteringTariffEditPopUp
                open={editPopUpOpen}
                data={editPopUpData}
                onCloseWithoutSaving={() => setEditPopUpOpen(false)}
                onCloseWithUpdate={handleEditPopUpClosedWithUpdate}
            />
            <Dialog open={alertOpen} onClose={() => setAlertOpen(false)}>
                <DialogTitle id="alert-dialog-title">
                    {t("Problem")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {alertMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAlertOpen(false)} autoFocus>
                        {t("OK")}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
            >
                <DialogTitle id="alert-dialog-title">
                    {t("Confirmation")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {t("Are you sure you want to delete this tariff?")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setDeleteConfirmOpen(false);
                            handleDelete(deleteCandidate);
                        }}
                    >
                        {t("YES")}
                    </Button>
                    <Button
                        onClick={() => setDeleteConfirmOpen(false)}
                        autoFocus
                    >
                        {t("NO")}
                    </Button>
                </DialogActions>
            </Dialog>
            <DataGrid
                rows={rows}
                columns={columns}
                components={{
                    Toolbar: TariffToolbar,
                }}
                componentsProps={{
                    toolbar: { onAddClick: handleAddClick },
                }}
                getRowId={(row) => row.id}
            />
        </>
    );
}
